// ============================================================================
// useAudioRecorder — record a voice clip via MediaRecorder for Flow dictation
// ============================================================================
// The clip is sent to the backend as a base64 data URI in the chat `audio`
// field; the backend transcribes it (Tinfoil voxtral) and folds the transcript
// into the message. Same record-and-transcribe model as iOS.

import { useCallback, useRef, useState } from 'react'

/** Prefer a container the backend transcriber handles well. Safari records
 *  audio/mp4 (→ m4a server-side); Chrome/Firefox only do webm/ogg (handled by
 *  `_audio_ext_for_mime`). Returns undefined to let the browser pick. */
function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return undefined
  const candidates = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus']
  return candidates.find(c => MediaRecorder.isTypeSupported(c))
}

function blobToDataUri(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read audio'))
    reader.readAsDataURL(blob)
  })
}

export interface UseAudioRecorder {
  isRecording: boolean
  isSupported: boolean
  error: string | null
  /** Begin capturing. Prompts for mic permission on first use. */
  start: () => Promise<void>
  /** Stop capturing and resolve the recorded clip as a base64 data URI (or
   *  null if nothing usable was captured). */
  stop: () => Promise<string | null>
  /** Abort an in-progress recording, discarding audio. */
  cancel: () => void
  clearError: () => void
}

export function useAudioRecorder(): UseAudioRecorder {
  const [isRecording, setIsRecording] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const streamRef = useRef<MediaStream | null>(null)

  const isSupported =
    typeof MediaRecorder !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia

  const teardownStream = useCallback(() => {
    streamRef.current?.getTracks().forEach(t => t.stop())
    streamRef.current = null
  }, [])

  const start = useCallback(async () => {
    if (!isSupported) {
      setError('Voice recording is not supported in this browser.')
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      const mimeType = pickMimeType()
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
      chunksRef.current = []
      recorder.ondataavailable = e => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }
      recorderRef.current = recorder
      recorder.start()
      setError(null)
      setIsRecording(true)
    } catch {
      teardownStream()
      setError('Microphone access was denied.')
    }
  }, [isSupported, teardownStream])

  const stop = useCallback(async (): Promise<string | null> => {
    const recorder = recorderRef.current
    if (!recorder) return null
    const done = new Promise<Blob>(resolve => {
      recorder.onstop = () =>
        resolve(new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' }))
    })
    recorder.stop()
    setIsRecording(false)
    const blob = await done
    teardownStream()
    recorderRef.current = null
    if (blob.size === 0) return null
    return blobToDataUri(blob)
  }, [teardownStream])

  const cancel = useCallback(() => {
    const recorder = recorderRef.current
    if (recorder && recorder.state !== 'inactive') {
      recorder.onstop = null
      recorder.stop()
    }
    chunksRef.current = []
    recorderRef.current = null
    teardownStream()
    setIsRecording(false)
  }, [teardownStream])

  const clearError = useCallback(() => setError(null), [])

  return { isRecording, isSupported, error, start, stop, cancel, clearError }
}
